import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable} from "rxjs";
import Office from "@core/shared/office.instance";
import { hexColorToRgb } from "@utils/index";
import {logger} from "@services/logger/logger.service";
import { Core } from "@core/core";

const HANDLER_NAME_FOR_THEME_CHANGE = 'officeThemeChanged';

export const THEME_TYPE = {
    COLOR_FULL: 0,
    DARK_GREY: 1,
    DARK: 2,
    WHITE: 3,
} as const;

export type IThemeType = (typeof THEME_TYPE)[keyof typeof THEME_TYPE];

const THEME_SETTING = {
    [THEME_TYPE.WHITE]: {
        className: '',
        maxColorSum: 255
    },
    [THEME_TYPE.COLOR_FULL]: {
        className: '',
        maxColorSum: 255
    },
    [THEME_TYPE.DARK_GREY]: {
        className: 'dark-theme',
        maxColorSum: 160,
    },
    [THEME_TYPE.DARK]: {
        className: 'dark-theme',
        maxColorSum: 80,
    },
} as const

interface IOfficeTheme extends Office.OfficeTheme {
    themeId: IThemeType
}

export interface IOfficeThemeChangedEventArgs {
    type: typeof HANDLER_NAME_FOR_THEME_CHANGE,
    officeTheme: IOfficeTheme
}

interface ITheme {
    type: IThemeType
}

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    private theme: BehaviorSubject<ITheme> = new BehaviorSubject<ITheme>({ type: THEME_TYPE.WHITE });
    private storageKey = 'r2-copilot-theme';
    private storage = window.localStorage;

    constructor(
        public core: Core
    ) {
        this.initTheme()
        this.initListener();
    }

    public toggleTheme(themeType: IThemeType) {
        if (typeof themeType === 'undefined') {
            return;
        }

        const themeSetting = THEME_SETTING[themeType] || THEME_SETTING[THEME_TYPE.WHITE];

        for (const themeSetting of Object.values(THEME_SETTING)) {
            const { className } = themeSetting
            if (document.documentElement.classList.contains(className)) {
                document.documentElement.classList.remove(className);
            }
        }

        if (themeSetting.className.length) {
            document.documentElement.classList.add(themeSetting.className);
        }

        this.saveThemeToStorage(themeType);
        this.saveThemeToMemory(themeType);
    }

    public $getTheme(): Observable<ITheme> {
        return this.theme.asObservable();
    }

    public async isThemeSupport(): Promise<boolean> {
        const platformInfo = this.core.env.get();
        return platformInfo.platform === Office.PlatformType.PC;
    }

    private async initTheme(): Promise<void> {
        const hasThemeToStorage = await this.getThemeFromStorage()
        if (hasThemeToStorage) {
            this.toggleTheme(hasThemeToStorage)
        }
    }

    private async getThemeFromStorage(): Promise<IThemeType | null> {
        if (await this.isThemeSupport()) {
            const officeTheme = Office.context.officeTheme as (IOfficeTheme | undefined);
            if (officeTheme && !officeTheme.themeId) {
                return this.getThemeByColors(Office.context.officeTheme)
            }

            if (officeTheme && officeTheme.themeId && typeof officeTheme.themeId === 'string') {
                return this.getThemeByColors(Office.context.officeTheme)
            }

            return officeTheme && officeTheme.themeId ? officeTheme.themeId : THEME_TYPE.WHITE
        }

        const themeType = this.storage.getItem(this.storageKey);
        if (themeType) {
            return Number(themeType) as IThemeType;
        }

        return null;
    }

    private saveThemeToStorage(themeType: IThemeType): void {
        const storage = window.localStorage
        storage.setItem(this.storageKey, themeType.toString())
    }

    private saveThemeToMemory(themeType: IThemeType): void {
        this.theme.next({ type: themeType });
    }

    private async initListener(): Promise<void> {
        await Office.onReady();
        const themeSupport = await this.isThemeSupport();
        if (!themeSupport || !Office.context?.mailbox) {
            return
        }

        Office.context.mailbox.addHandlerAsync(HANDLER_NAME_FOR_THEME_CHANGE, (event: IOfficeThemeChangedEventArgs) => {
            if (event.type === HANDLER_NAME_FOR_THEME_CHANGE) {
                const themeType = this.getThemeByColors(Office.context.officeTheme);
                this.toggleTheme(themeType)
            }
        }, {}, (result: Office.AsyncResult<void>) => {
            if (result.status == Office.AsyncResultStatus.Failed) {
                logger.error("addHandlerAsync(officeThemeChanged) error: " + JSON.stringify(result.error));
            }
        });
    }

    private getThemeByColors(themeColors: Office.OfficeTheme): IThemeType {
        try {
            const markColor = hexColorToRgb(themeColors.bodyBackgroundColor);
            const middleColor = (markColor.r + markColor.g + markColor.b) / 3;

            if (middleColor <= THEME_SETTING[THEME_TYPE.DARK].maxColorSum) {
                return THEME_TYPE.DARK;
            }

            if (middleColor <= THEME_SETTING[THEME_TYPE.DARK_GREY].maxColorSum) {
                return THEME_TYPE.DARK_GREY;
            }

            if (middleColor <= THEME_SETTING[THEME_TYPE.COLOR_FULL].maxColorSum) {
                return THEME_TYPE.COLOR_FULL;
            }

            return THEME_TYPE.WHITE
        } catch (e) {
            logger.error(e)
            return THEME_TYPE.WHITE
        }
    }
}
